import React, { useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, MenuItem, Select, TextField, Typography, Avatar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { SessionServiceHeader } from "./SessionServiceHeader";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupsIcon from "@mui/icons-material/Groups";
import HistoryIcon from "@mui/icons-material/History";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import dayjs from "dayjs";

/**
 * Unified session / individual-service details popup.
 * Used by Calendar (single event click + session group click), the client grid
 * (TableTab), and the nav-bar alert bell.  Presentational — action buttons only
 * render when their callback prop is supplied.
 *
 * Props:
 *   open, session, onClose
 *   AvatarComponent      – optional ({ clientId }) => node  (provides photo avatars)
 *   onOpenClient(clientId, name)
 *   onEditService(clientEvent)          – per-client edit pencil
 *   onOpenClientForm(clientEvent)       – per-client form icon (only when client has a form)
 *   onViewHistory(clientEvent)          – per-client history icon
 *   onGroupForm(session)                – "Session Form" button (multi-client)
 *   onCompleteSession(session)          – "Complete Group Session" button
 *   onSaveTime(clientEvent, start, end) – enables inline Edit-Time for single-client editable events
 *
 * session shape:
 *   { title, start, end, allDay, serviceId, isEditable, providerName,
 *     clients: [{ id, clientId, clientFirstName, clientLastName, clientName,
 *                 levelOfCare, attendanceStatus, serviceStatus/statusId,
 *                 isEditable, psychosocialAssessmentFormId, user, start, end }] }
 */
const InitialsAvatar = ({ name }) => {
  const initials = (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  return <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: "#1976d2" }}>{initials || "?"}</Avatar>;
};

export const SessionInfoDialog = ({
  open,
  session,
  onClose,
  AvatarComponent,
  onOpenClient,
  onEditService,
  onOpenClientForm,
  onViewHistory,
  onGroupForm,
  onCompleteSession,
  onViewCalendar,                      // (session) => void — "View on Calendar" (opens the full calendar for this client at this week)
  onSaveTime,
  attendanceOptions = [],              // [{id, name}] — when set with onSetAttendance, shows a per-client attendance picker
  onSetAttendance,                     // (clientEvent, attendanceStatusId|null) => void
  onMarkPerformed,                     // (clientEvent) => void — quick "mark this service performed"
  markPerformedBusyId = null,          // clientServiceId currently being checked/marked (disables its button)
}) => {
  // Statuses that are already delivered/closed — no "mark performed" action offered.
  // The services query returns the status NAME (not StatusId), so match on either.
  const DELIVERED_STATUS_NAMES = ["performed", "pending approval", "approved", "billed", "closed"];
  const isPerformedOrLater = (c) =>
    [2, 3, 4, 5, 6].includes(Number(c?.statusId)) ||
    DELIVERED_STATUS_NAMES.includes(String(c?.serviceStatus ?? "").toLowerCase());

  // The services query returns the attendance NAME (string), not its id, so resolve the current
  // dropdown value from the name → option id (falling back to any id present). This makes the
  // dropdown show the current status and reflect a change immediately (the optimistic update and
  // the refetched row both carry the name).
  const resolveAttendanceId = (c) => {
    if (c?.attendanceStatusId != null && c.attendanceStatusId !== "") return c.attendanceStatusId;
    const match = attendanceOptions.find((o) => o.name === c?.attendanceStatus);
    return match ? match.id : "";
  };

  // Service (care) status only. Billing/authorization status is intentionally NOT shown here —
  // the calendar is the care provider's view; billing and authorizations are handled by an admin.
  const SERVICE_STATUS_COLORS = {
    scheduled: { bg: "#eef7ff", fg: "#2c5f7d" },
    performed: { bg: "#e6f4ea", fg: "#1e7d34" },
    "pending approval": { bg: "#fff4e5", fg: "#b26a00" },
    approved: { bg: "#e6f4ea", fg: "#1e7d34" },
    billed: { bg: "#f3f2ea", fg: "#6b6a52" },
    closed: { bg: "#f3f2ea", fg: "#6b6a52" },
    void: { bg: "#fdecea", fg: "#c0392b" },
    canceled: { bg: "#fdecea", fg: "#c0392b" },
    cancelled: { bg: "#fdecea", fg: "#c0392b" },
  };
  // Prefer the status name from the services query; fall back to the numeric status id.
  const STATUS_BY_ID = {
    1: "Scheduled", 2: "Performed", 3: "Pending Approval", 4: "Approved",
    5: "Billed", 6: "Closed", 7: "Void", 8: "Canceled",
  };
  const serviceStatusOf = (c) =>
    c?.serviceStatus || STATUS_BY_ID[Number(c?.statusId)] || null;
  const serviceStatusStyle = (name) =>
    SERVICE_STATUS_COLORS[String(name ?? "").toLowerCase()] ?? { bg: "#f5f5f5", fg: "#424242" };
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");

  if (!session) return null;

  const clients = session.clients ?? [];
  const isGroup = clients.length > 1;
  const isSingle = clients.length === 1;
  const nameOf = (c) =>
    `${c.clientFirstName ?? ""} ${c.clientLastName ?? ""}`.trim() || c.clientName || "Client";

  const start = session.start instanceof Date ? session.start : new Date(session.start);
  const end = session.end ? (session.end instanceof Date ? session.end : new Date(session.end)) : null;

  // Single-client: the client's own start/end may differ from the session's when used
  // from the TableTab (service.startDate vs session.start are the same thing here).
  const singleClient = isSingle ? clients[0] : null;
  const singleEditable = !!(singleClient?.isEditable && onSaveTime);

  const handleClose = () => {
    setIsEditingTime(false);
    onClose?.();
  };

  const handleSaveTime = () => {
    onSaveTime?.(singleClient, editStart, editEnd);
    setIsEditingTime(false);
  };

  // Resolve provider name: explicit session.providerName takes priority; for single-client
  // events the user field is on the client object.
  const providerName =
    session.providerName ||
    (singleClient?.user
      ? `${singleClient.user.firstName ?? ""} ${singleClient.user.lastName ?? ""}`.trim()
      : null);
  const providerRole = singleClient?.user?.roleName || singleClient?.user?.role || clients[0]?.user?.roleName;

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      {/* Bloom-styled service header, coloured to the selected service type */}
      <Box sx={{ position: "relative" }}>
        <SessionServiceHeader
          title={session.title}
          subtitle={isGroup ? `Group session · ${clients.length} clients` : "Individual session"}
        />
        <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8, color: "#5a5740" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        {/* ── Date / Time (with optional inline edit for single editable events) ── */}
        <Box sx={{ display: "flex", alignItems: isEditingTime ? "flex-start" : "center", gap: 1, mb: 1.5 }}>
          <CalendarMonthIcon fontSize="small" color="action" sx={{ mt: isEditingTime ? "10px" : 0, flexShrink: 0 }} />
          {isEditingTime ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
              <TextField label="Start" type="datetime-local" size="small" fullWidth
                value={editStart} onChange={e => setEditStart(e.target.value)}
                InputLabelProps={{ shrink: true }} />
              <TextField label="End" type="datetime-local" size="small" fullWidth
                value={editEnd} onChange={e => setEditEnd(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {dayjs(start).format(session.allDay ? "dddd, MMMM D, YYYY" : "dddd, MMMM D, YYYY · h:mm A")}
              {!session.allDay && end && <>{" – "}{dayjs(end).format("h:mm A")}</>}
            </Typography>
          )}
        </Box>

        {/* ── Provider ── */}
        {providerName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
            <BadgeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
              {providerName}
              {providerRole && (
                <Typography component="span" sx={{ fontSize: "11px", color: "text.secondary", ml: 0.5 }}>
                  · {providerRole}
                </Typography>
              )}
            </Typography>
          </Box>
        )}

        {/* ── Level of care (single client) ── */}
        {isSingle && singleClient.levelOfCare && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <LocalHospitalIcon fontSize="small" color="action" />
            <Typography variant="body2">{singleClient.levelOfCare}</Typography>
          </Box>
        )}

        {/* ── Attendance / Billing (single client) ── */}
        {isSingle && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
            <EventAvailableIcon fontSize="small" color="action" />
            <Typography variant="body2">{singleClient.attendanceStatus || "N/A"}</Typography>
          </Box>
        )}
        {isSingle && serviceStatusOf(singleClient) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <AssignmentTurnedInIcon fontSize="small" color="action" />
            <Chip
              label={serviceStatusOf(singleClient)}
              size="small"
              sx={{
                fontSize: "11px", height: 22, fontWeight: 600,
                bgcolor: serviceStatusStyle(serviceStatusOf(singleClient)).bg,
                color: serviceStatusStyle(serviceStatusOf(singleClient)).fg,
              }}
            />
          </Box>
        )}

        {/* ── Client list (always shown; shows 1 row for single-client) ── */}
        {isGroup && (
          <Typography sx={{ fontWeight: 600, fontSize: "13px", mb: 1 }}>
            {clients.length} Clients in this session
          </Typography>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {clients.map((c) => (
            <Box
              key={c.id ?? c.clientId}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                px: 1.5, py: 1, border: "1px solid #e0e0e0", borderRadius: 1,
                cursor: onOpenClient ? "pointer" : "default",
                "&:hover": onOpenClient ? { bgcolor: "#f5f5f5" } : undefined,
              }}
              onClick={onOpenClient ? () => onOpenClient(c.clientId, nameOf(c)) : undefined}
            >
              {AvatarComponent ? <AvatarComponent clientId={c.clientId} /> : <InitialsAvatar name={nameOf(c)} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: "13px", fontWeight: 500,
                    ...(onOpenClient ? { color: "primary.main", textDecoration: "underline" } : {}) }}>
                  {nameOf(c)}
                </Typography>
                {c.levelOfCare && isGroup && (
                  <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>{c.levelOfCare}</Typography>
                )}
                {c.isTransferredOpen && (
                  <Typography sx={{ fontSize: "11px", color: "#8a5a00", fontWeight: 600 }}>
                    ↪ Transferred from {c.transferredFromName}
                    {c.transferredDate ? ` on ${new Date(c.transferredDate).toLocaleDateString()}` : ""}
                    {c.end && new Date(c.end) < new Date() ? " — overdue, needs completion" : " — needs completion"}
                  </Typography>
                )}
              </Box>
              {/* Status chips + attendance picker */}
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "nowrap", alignItems: "center" }}
                onClick={e => e.stopPropagation()}>
                {onSetAttendance && attendanceOptions.length > 0 ? (
                  <Select
                    size="small"
                    displayEmpty
                    value={resolveAttendanceId(c)}
                    onChange={(e) => onSetAttendance(c, e.target.value === "" ? null : Number(e.target.value))}
                    renderValue={(v) => v === "" ? "Attendance" : (attendanceOptions.find(o => Number(o.id) === Number(v))?.name ?? "Attendance")}
                    sx={{ fontSize: "11px", height: 26, minWidth: 120, "& .MuiSelect-select": { py: 0.25 } }}
                  >
                    <MenuItem value="" sx={{ fontSize: "12px" }}><em>Not set</em></MenuItem>
                    {attendanceOptions.map((o) => (
                      <MenuItem key={o.id} value={o.id} sx={{ fontSize: "12px" }}>{o.name}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  c.attendanceStatus && isGroup && (
                    <Chip label={c.attendanceStatus} size="small" sx={{ fontSize: "10px" }} />
                  )
                )}
                {serviceStatusOf(c) && (
                  <Chip
                    label={serviceStatusOf(c)}
                    size="small"
                    sx={{
                      fontSize: "10px", fontWeight: 600,
                      bgcolor: serviceStatusStyle(serviceStatusOf(c)).bg,
                      color: serviceStatusStyle(serviceStatusOf(c)).fg,
                    }}
                  />
                )}
              </Box>
              {/* Action icons */}
              {(onEditService || onViewHistory || onMarkPerformed || (onOpenClientForm && c.psychosocialAssessmentFormId)) && (
                <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  {onMarkPerformed && !isPerformedOrLater(c) && (
                    <IconButton
                      size="small"
                      title="Mark performed"
                      onClick={() => onMarkPerformed(c)}
                      disabled={markPerformedBusyId === c.id}
                      sx={{ color: "#2e7d32" }}
                    >
                      <TaskAltIcon fontSize="small" />
                    </IconButton>
                  )}
                  {onEditService && (
                    <IconButton size="small" title="Edit this service" onClick={() => onEditService(c)} sx={{ color: "#4281AB" }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {onOpenClientForm && c.psychosocialAssessmentFormId && (
                    <IconButton size="small" title="Open form" onClick={() => onOpenClientForm(c)} sx={{ color: "#b26a00" }}>
                      <DescriptionIcon fontSize="small" />
                    </IconButton>
                  )}
                  {onViewHistory && (
                    <IconButton size="small" title="Status history" onClick={() => onViewHistory(c)} sx={{ color: "#4281AB" }}>
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
        {onViewCalendar && (
          <Button size="small" variant="outlined" startIcon={<CalendarMonthIcon />} onClick={() => onViewCalendar(session)}>
            View on Calendar
          </Button>
        )}
        {/* Single-client: Edit Time */}
        {singleEditable && (
          isEditingTime ? (
            <>
              <Button size="small" variant="contained" onClick={handleSaveTime}>Save Time</Button>
              <Button size="small" variant="outlined" onClick={() => setIsEditingTime(false)}>Cancel</Button>
            </>
          ) : (
            <Button size="small" variant="outlined" onClick={() => {
              const evtStart = singleClient.start ?? session.start;
              const evtEnd = singleClient.end ?? session.end;
              setEditStart(dayjs(evtStart).format("YYYY-MM-DDTHH:mm"));
              setEditEnd(dayjs(evtEnd).format("YYYY-MM-DDTHH:mm"));
              setIsEditingTime(true);
            }}>
              Edit Time
            </Button>
          )
        )}
        {/* Multi-client: group session form */}
        {onGroupForm && isGroup && clients.some((c) => c.psychosocialAssessmentFormId) && (
          <Button variant="contained" color="success" startIcon={<GroupsIcon />} onClick={() => onGroupForm(session)}>
            Session Form
          </Button>
        )}
        {/* Multi-client: complete group session */}
        {onCompleteSession && session.isEditable && (
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => onCompleteSession(session)}>
            Complete Group Session
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
